import React from "react";
import { useNavigate, Link } from "react-router-dom";

const FEATURES = [
  { icon: "💬", title: "Conversa com IA", text: "Fale sobre seus desafios. A IA entende seus bloqueios e adapta seu plano de forma humana." },
  { icon: "📋", title: "Planos salvos no app", text: "Cada conversa gera um plano de ação com insights e próximos passos, sempre à mão na aba Meus Planos." },
  { icon: "📊", title: "Dashboards Inteligentes", text: "Acompanhe seu progresso por dia, semana e mês. Veja padrões que nunca percebeu." },
  { icon: "🎯", title: "Monitoramento de Compromissos", text: "Lance atividades e acompanhe sua taxa de cumprimento, com prioridades e prazos claros." },
  { icon: "🧠", title: "Módulos de Autoconhecimento", text: "Jornadas de identificação de padrões, reframe mental e criação de hábitos duradouros." },
  { icon: "🔔", title: "Lembretes no WhatsApp", text: "Atividades atrasadas ou pendentes chegam direto no seu WhatsApp — sem precisar abrir o app pra lembrar." },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#EEF2FF] text-gray-900" data-testid="landing-page">
      {/* NAVBAR */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 sm:px-8 py-3 bg-[#EEF2FF]/90 backdrop-blur-xl">
        <div className="flex items-center gap-2 font-outfit font-bold text-lg">
          <div className="w-8 h-8 rounded-lg bg-[#1E3A8A] text-white flex items-center justify-center shadow-[0_0_15px_rgba(30,58,138,0.35)]">✦</div>
          Lumis
        </div>
        <div className="flex gap-2">
          <Link to="/login" data-testid="landing-nav-login" className="px-4 py-2 rounded-xl border border-gray-300 text-sm font-manrope font-medium hover:border-[#1E3A8A]/50 transition-colors">
            Entrar
          </Link>
          <Link to="/register" data-testid="landing-nav-register" className="px-4 py-2 rounded-xl bg-[#1E3A8A] text-white text-sm font-manrope font-medium hover:bg-[#172554] transition-colors shadow-[0_4px_15px_rgba(30,58,138,0.35)]">
            Inscreva-se
          </Link>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative flex items-center justify-center text-center px-6 pt-32 pb-12 min-h-[90vh] overflow-hidden">
        <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(30,58,138,0.08)_0%,transparent_70%)] pointer-events-none" />
        <div className="relative max-w-xl space-y-6">
          <div className="inline-flex items-center gap-1.5 bg-[#DBEAFE] border border-[#1E3A8A]/20 px-4 py-1.5 rounded-full text-sm text-[#1E3A8A] font-manrope font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-[#10b981] animate-pulse" />
            📲 Com lembretes direto no seu WhatsApp
          </div>
          <h1 className="font-outfit font-black text-4xl sm:text-5xl leading-tight">
            Pare de{" "}
            <span className="bg-gradient-to-br from-[#1E3A8A] to-[#2563eb] bg-clip-text text-transparent">adiar</span>{" "}
            sua vida.
            <br />
            Seu WhatsApp vai cobrar até você fazer.
          </h1>
          <p className="text-gray-500 text-base sm:text-lg font-manrope leading-relaxed max-w-md mx-auto">
            Crie sua Agenda Antiprocrastinação, defina o período dos seus compromissos, e receba lembretes e dicas no WhatsApp — na hora certa, e de novo quando você não cumprir.
          </p>
          <button
            data-testid="landing-hero-cta"
            onClick={() => navigate("/register")}
            className="px-8 py-4 rounded-xl bg-[#1E3A8A] text-white font-manrope font-semibold text-base hover:bg-[#172554] hover:-translate-y-0.5 transition-all shadow-[0_4px_15px_rgba(30,58,138,0.35)]"
          >
            Criar Minha Agenda Grátis →
          </button>
          <p className="text-gray-400 text-sm font-manrope">10 dias grátis • Sem cartão de crédito • Primeiro lembrete no WhatsApp em minutos</p>
        </div>
      </section>

      {/* PROBLEM BAR */}
      <section className="text-center px-6 py-10 border-y border-gray-200 bg-gradient-to-br from-[#1E3A8A]/[0.06] to-[#2563eb]/[0.04]">
        <h2 className="font-outfit font-extrabold text-xl sm:text-2xl leading-snug mb-3">
          Você já sabe o que precisa fazer.<br />O problema é fazer na hora certa.
        </h2>
        <p className="text-gray-500 font-manrope max-w-lg mx-auto leading-relaxed">
          Agenda, alarme, lista de tarefas — você já tentou de tudo. O problema não é lembrar o que fazer, é lembrar <strong className="text-gray-900">quando fazer</strong> e não deixar pra depois assim que a preguiça ou a desculpa aparece.
        </p>
        <div className="inline-flex items-center gap-1.5 mt-4 bg-red-50 border border-red-200 px-4 py-1.5 rounded-full text-sm text-red-700 font-manrope font-semibold">
          ⚠️ Sem cobrança externa, procrastinar é sempre mais fácil que agir — e isso custa horas da sua semana sem você perceber
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section className="max-w-5xl mx-auto px-6 py-14">
        <h2 className="text-center font-outfit font-extrabold text-2xl mb-10">Como Funciona</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {[
            { n: "1", title: "Crie sua agenda", text: "Diga seus compromissos e o período — a IA monta o plano com você." },
            { n: "2", title: "Receba no WhatsApp", text: "Lembrete automático na hora certa. Sem precisar abrir o app." },
            { n: "3", title: "Não cumpriu? A gente te ajuda", text: "Se você não marcar como feito, o WhatsApp te manda uma dica pra retomar — sem julgamento, só o empurrão certo." },
          ].map((s, i) => (
            <div key={i} data-testid={`landing-how-it-works-${i}`} className="bg-white border border-gray-200 rounded-2xl p-7 text-center shadow-sm">
              <div className="w-12 h-12 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#1e40af] flex items-center justify-center text-lg font-outfit font-extrabold text-white shadow-[0_8px_20px_rgba(30,58,138,0.25)]">
                {s.n}
              </div>
              <h3 className="font-manrope font-bold mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm font-manrope leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* APP PREVIEW */}
      <section className="flex justify-center px-6 py-14">
        <div className="w-[260px] bg-white border-2 border-gray-200 rounded-[28px] overflow-hidden shadow-[0_25px_50px_rgba(0,0,0,0.12),0_0_40px_rgba(30,58,138,0.06)]">
          <div className="w-24 h-5 bg-gray-100 rounded-b-2xl mx-auto" />
          <div className="px-3 pb-5 pt-2 font-manrope bg-[#EEF2FF]">
            <div className="flex items-center justify-between text-[10px] text-gray-400 mb-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1E3A8A]" />
              <span>Lumis</span>
              <span>🔍 ⋯</span>
            </div>
            <div className="flex items-center justify-between font-bold text-base mb-2 text-gray-900">
              Agenda <span className="text-gray-400 text-sm">↗</span>
            </div>
            <div className="flex gap-1.5 mb-2">
              <div className="px-2 py-1 rounded-lg border border-gray-200 bg-white text-gray-500 text-[9px] font-semibold">💬 Desenvolva-se</div>
              <div className="px-2 py-1 rounded-lg bg-[#1E3A8A] text-white text-[9px] font-semibold">+ Nova Atividade</div>
            </div>
            <div className="text-center text-[9px] text-gray-400 mb-2">Elimine a procrastinação com Organização!</div>
            <div className="rounded-xl p-1.5 mb-2 space-y-1" style={{ backgroundColor: "#1E3A8A" }}>
              <div className="bg-white rounded-lg py-1.5 text-center text-[9px] font-semibold text-gray-900">Hábitos para Vencer a Procrastinação</div>
              <div className="bg-white rounded-lg py-1.5 text-center text-[9px] font-semibold text-gray-900">Aumentar a Produtividade</div>
            </div>
            <div className="grid grid-cols-2 gap-1.5 mb-2">
              <div className="bg-white rounded-lg p-1.5 text-center border border-gray-100"><div className="text-[8px] text-gray-400">Pendentes</div><div className="text-sm font-extrabold text-[#1E3A8A]">0</div></div>
              <div className="bg-white rounded-lg p-1.5 text-center border border-gray-100"><div className="text-[8px] text-gray-400">Em Andamento</div><div className="text-sm font-extrabold" style={{ color: "#2563eb" }}>0</div></div>
              <div className="bg-white rounded-lg p-1.5 text-center border border-gray-100"><div className="text-[8px] text-gray-400">Atrasadas</div><div className="text-sm font-extrabold text-red-500">0</div></div>
              <div className="bg-white rounded-lg p-1.5 text-center border border-gray-100"><div className="text-[8px] text-gray-400">Concluídas</div><div className="text-sm font-extrabold text-emerald-500">0</div></div>
            </div>
            <div className="flex gap-1 mb-2">
              <div className="px-2 py-0.5 rounded-lg bg-[#1E3A8A] text-white text-[8px] font-semibold">Pendentes</div>
              <div className="px-2 py-0.5 rounded-lg bg-white border border-gray-200 text-gray-400 text-[8px] font-semibold">Concluídas</div>
              <div className="px-2 py-0.5 rounded-lg bg-white border border-gray-200 text-gray-400 text-[8px] font-semibold">Todas</div>
            </div>
            <div className="text-center py-3">
              <div className="text-lg text-gray-300">☰</div>
              <p className="text-[9px] text-gray-400">Nenhuma atividade encontrada</p>
            </div>
          </div>
        </div>
      </section>

      {/* PROVA SOCIAL */}
      <section className="text-center px-6 py-8">
        <div className="inline-flex items-center gap-1.5 bg-[#DBEAFE] border border-[#1E3A8A]/20 px-4 py-1.5 rounded-full text-sm text-[#1E3A8A] font-manrope font-medium">
          ✦ Testado por pessoas reais durante o desenvolvimento do Lumis
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-5xl mx-auto px-6 py-14">
        <h2 className="text-center font-outfit font-extrabold text-2xl mb-10">Principais Recursos</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <div key={i} data-testid={`landing-feature-${i}`} className="bg-white border border-gray-200 rounded-2xl p-7 text-center hover:border-[#1E3A8A] hover:-translate-y-1 transition-all shadow-sm">
              <div className="w-12 h-12 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#1e40af] flex items-center justify-center text-xl shadow-[0_8px_20px_rgba(30,58,138,0.25)]">
                {f.icon}
              </div>
              <h3 className="font-manrope font-bold mb-2">{f.title}</h3>
              <p className="text-gray-500 text-sm font-manrope leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="relative text-center px-6 py-16">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(30,58,138,0.05)_0%,transparent_60%)] pointer-events-none" />
        <div className="relative">
          <h2 className="font-outfit font-black text-2xl sm:text-3xl mb-3">Sua vida não vai se organizar sozinha.<br />Mas seu WhatsApp pode te lembrar até ela se organizar.</h2>
          <p className="text-gray-500 font-manrope text-lg mb-6">Crie sua agenda agora e receba seu primeiro lembrete ainda hoje.</p>
          <button
            data-testid="landing-final-cta"
            onClick={() => navigate("/register")}
            className="px-8 py-4 rounded-xl bg-[#1E3A8A] text-white font-manrope font-semibold text-base hover:bg-[#172554] hover:-translate-y-0.5 transition-all shadow-[0_4px_15px_rgba(30,58,138,0.35)]"
          >
            Criar Minha Agenda Grátis →
          </button>
          <p className="text-gray-400 text-sm font-manrope mt-4">✓ 10 dias grátis • Depois R$19,00/mês • Cancele quando quiser</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="text-center px-6 py-8 border-t border-gray-200 space-y-2">
        <p className="text-gray-400 text-sm font-manrope">
          ou{" "}
          <Link to="/onboarding" className="text-gray-500 hover:text-[#1E3A8A] underline" data-testid="landing-modules-link">
            explore nossos módulos de autoconhecimento
          </Link>
        </p>
        <p className="text-gray-400 text-sm font-manrope">© 2026 Lumis. Autoconhecimento e produtividade com IA.</p>
      </footer>
    </div>
  );
}
